import * as React from 'react';
import { useState } from 'react';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Container, List, ListItem, Skeleton } from '@mui/material';
import ProductDenomination from './ProductDenomination';
import '../styles/productDetailTabView.css';
import ProductRedeemVideo from './ProductRedeemVideo';
import { useSelector } from 'react-redux';

function CustomTabPanel(props) {
    const { children, value, index, outOfStock, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ px: 0, pt: { md: 1 } }}>{children}</Box>}
        </div>
    );
}

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const ProductDetailTabView = ({ pageLoading }) => {
    const [value, setValue] = useState(0);
    const PRODUCTDETAILS = useSelector((state) => state.ProductDetail?.allData);
    const handleChange = (event, newValue) => {
        event.preventDefault();
        setValue(newValue);
    };

    React.useEffect(() => {
        if (value !== 0) setValue(0);
    }, [pageLoading]);

    // Show Loading Skeleton
    if (pageLoading) {
        return (
            <Skeleton
                variant="rectangular"
                animation="wave"
                width="100%"
                height="350px"
                sx={{
                    mt: {
                        xs: 3,
                        lg: 0,
                    },
                    mb: {
                        xs: 3,
                    },
                    borderRadius: 2,
                    boxShadow: 3,
                }}
            ></Skeleton>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{
                mb: 1,
                borderBottom: 1,
                borderColor: 'linear-gradient(180deg, #0B4AA2 0%, #0A3C84 100%)',
            }}>
                <Tabs
                    value={value}
                    onChange={handleChange}
                    aria-label="basic tabs example"
                    variant="scrollable"
                    scrollButtons="auto"
                >{[
                    {
                        label: "Overview",
                        value: 0,
                    },
                    {
                        label: "Redeem Video",
                        value: 1,
                    },
                    {
                        label: "More Info",
                        value: 2,
                    }
                ].map((tab) => (
                    <Tab
                        key={tab.value}
                        label={
                            <Typography
                                variant="body1"
                                sx={[
                                    {
                                        fontSize: '1.1rem',
                                        fontWeight: 400,
                                        px: 2,
                                        py: 1,
                                        borderRadius: '5px',
                                    },
                                    value === tab.value && {
                                        color: 'white.main',
                                        background: 'linear-gradient(180deg, #0B4AA2 0%, #0A3C84 100%)',
                                    },
                                ]}
                            >
                                {tab.label}
                            </Typography>
                        }
                        {...a11yProps(tab.value)}
                        sx={{
                            textTransform: 'capitalize',
                            p: 0,
                        }}
                    />
                ))}
                </Tabs>
            </Box>

            <CustomTabPanel value={value} index={0}>
                {PRODUCTDETAILS.outOfStock ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: 'white.light',
                            minHeight: {
                                xs: '40px',
                                sm: '40px',
                                lg: '60px',
                            },
                            borderRadius: {
                                xs: 1,
                            },
                        }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: {
                                    xs: '1.2vw',
                                    sm: '1.62vw',
                                    lg: '1.8vw',
                                },
                                color: 'primary.main',
                            }}
                        >
                            Out of Stock
                        </Typography>
                    </Box>
                ) : (
                    <ProductDenomination />
                )}
            </CustomTabPanel>

            <CustomTabPanel value={value} index={1}>
                {PRODUCTDETAILS?.youtube ? (
                    <ProductRedeemVideo videoId={PRODUCTDETAILS?.youtube} />
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                width: {
                                    lg: '40vw',
                                },
                                height: '130px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: '22px',
                                    fontWeight: 500,
                                    color: 'primary.main',
                                }}
                            >
                                ! No Video Available
                            </Typography>
                        </Box>
                    </Box>
                )}
            </CustomTabPanel>

            <CustomTabPanel value={value} index={2}>
                {PRODUCTDETAILS?.moreInfo?.trim()?.length ? (
                    <List>
                        <ListItem>
                            <Box
                                component="div"
                                dangerouslySetInnerHTML={{ __html: PRODUCTDETAILS?.['moreInfo'] || null }}
                                style={{
                                    whiteSpace: 'initial',
                                    wordWrap: 'break-word',
                                    overflowX: 'auto',
                                    maxWidth: '100%',
                                    boxSizing: 'border-box',
                                    wordSpacing: '0px',
                                    textIndent: 'revert-layer',
                                    fontFamily: 'Celias',
                                }}
                                sx={{
                                    mt: 1,
                                    fontSize: { xs: '0.9rem', lg: '1.2rem' },
                                    lineHeight: { xs: '16px', lg: '2.3rem' },
                                    color: 'dark.main',
                                    fontWeight: { xs: 400, lg: 400 },
                                }}
                            />
                        </ListItem>
                    </List>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                width: {
                                    lg: '40vw',
                                },
                                height: '130px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: '22px',
                                    fontWeight: 500,
                                    color: 'primary.main',
                                }}
                            >
                                ! More Infomation is Not Available
                            </Typography>
                        </Box>
                    </Box>
                )}
            </CustomTabPanel>
        </Container>
    );
};

export default ProductDetailTabView;
